import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Label,
  Select,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { getMyEngagement } from "../../services/resourceAllocation.service";
import type { IMyEngagementApi } from "../../services/types/resourceAllocation";
import { formatResourceAllocationDuration } from "../../utils/resourceAllocationDuration";
import {
  clampEngagementPeriodFromNumbers,
  getEngagementSelectableMonths,
  getEngagementSelectableYears,
  monthNameToNumber,
} from "../../utils/engagementPeriod";

interface EngagementHistoryDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialYear: number;
  initialMonth: number;
}

const EMPTY_ENGAGEMENT: IMyEngagementApi = {
  hasIncompleteWorkHours: false,
  year: 0,
  month: 0,
  totalOrderedMinutes: 0,
  lines: [],
};

const useStyles = makeStyles({
  surface: {
    maxWidth: "560px",
    width: "100%",
  },
  info: {
    marginBottom: "1rem",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "1rem",
    rowGap: "0.75rem",
    marginBottom: "1rem",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    rowGap: "0.25rem",
  },
  monthSelect: {
    minWidth: "140px",
  },
  yearSelect: {
    minWidth: "90px",
  },
  total: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    fontWeight: tokens.fontWeightSemibold,
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
    paddingBottom: "0.35rem",
    marginBottom: "0.5rem",
  },
  line: {
    display: "flex",
    justifyContent: "space-between",
    columnGap: "1rem",
    paddingBlock: "0.2rem",
  },
  lineMeta: {
    display: "flex",
    flexDirection: "column",
  },
  projectName: {
    fontWeight: tokens.fontWeightRegular,
  },
  managerName: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  lineHours: {
    whiteSpace: "nowrap",
    fontWeight: tokens.fontWeightSemibold,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    paddingBlock: "1rem",
    textAlign: "center",
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    paddingBlock: "2rem",
  },
});

const EngagementHistoryDialog: React.FC<EngagementHistoryDialogProps> = ({
  open,
  setOpen,
  initialYear,
  initialMonth,
}) => {
  const styles = useStyles();
  const selectableYears = getEngagementSelectableYears();
  const hasSelectablePeriod = selectableYears.length > 0;

  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [engagement, setEngagement] = useState<IMyEngagementApi>(EMPTY_ENGAGEMENT);
  const [isLoading, setIsLoading] = useState(false);

  const selectableMonths = getEngagementSelectableMonths(selectedYear);

  const loadEngagement = useCallback(async (year: number, month: number) => {
    if (getEngagementSelectableYears().length === 0) {
      setEngagement(EMPTY_ENGAGEMENT);
      return;
    }

    setIsLoading(true);
    try {
      const data = await getMyEngagement(year, month);
      setEngagement(data);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const { month, year } = clampEngagementPeriodFromNumbers(initialMonth, initialYear);
    setSelectedMonth(month);
    setSelectedYear(year);
    loadEngagement(year, monthNameToNumber(month));
  }, [open, initialYear, initialMonth, loadEngagement]);

  const onMonthSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const month = e.target.value;
    setSelectedMonth(month);
    loadEngagement(selectedYear, monthNameToNumber(month));
  };

  const onYearSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextYear = parseInt(e.target.value, 10);
    const monthsForYear = getEngagementSelectableMonths(nextYear);
    const nextMonth = monthsForYear.includes(selectedMonth)
      ? selectedMonth
      : monthsForYear[monthsForYear.length - 1] ?? selectedMonth;

    setSelectedYear(nextYear);
    setSelectedMonth(nextMonth);
    loadEngagement(nextYear, monthNameToNumber(nextMonth));
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Historia zaangażowania</DialogTitle>
          <DialogContent>
            <div className={styles.info}>
              Dane o zleconych godzinach dostępne są od 07.2026.
            </div>

            {hasSelectablePeriod ? (
              <div className={styles.filters}>
                <div className={styles.filterGroup}>
                  <Label size="small" htmlFor="engagement-history-month">
                    Miesiąc
                  </Label>
                  <Select
                    id="engagement-history-month"
                    className={styles.monthSelect}
                    onChange={onMonthSelect}
                    value={selectedMonth}
                  >
                    {selectableMonths.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className={styles.filterGroup}>
                  <Label size="small" htmlFor="engagement-history-year">
                    Rok
                  </Label>
                  <Select
                    id="engagement-history-year"
                    className={styles.yearSelect}
                    onChange={onYearSelect}
                    value={selectedYear}
                  >
                    {selectableYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            ) : null}

            {!hasSelectablePeriod ? (
              <div className={styles.empty}>Brak danych dla wybranego miesiąca.</div>
            ) : isLoading ? (
              <div className={styles.centered}>
                <Spinner label="Ładowanie..." />
              </div>
            ) : engagement.lines.length === 0 ? (
              <div className={styles.empty}>Brak danych dla wybranego miesiąca.</div>
            ) : (
              <>
                <div className={styles.total}>
                  <span>Zlecone godziny</span>
                  <span>
                    {formatResourceAllocationDuration(engagement.totalOrderedMinutes)}
                  </span>
                </div>
                {engagement.lines.map((line, index) => (
                  <div
                    key={`${line.youTrackProjectId}-${line.managerFullName}-${index}`}
                    className={styles.line}
                  >
                    <span className={styles.lineMeta}>
                      <span className={styles.projectName}>
                        {line.youTrackProjectLabel}
                      </span>
                      <span className={styles.managerName}>
                        Kierownik: {line.managerFullName}
                      </span>
                    </span>
                    <span className={styles.lineHours}>
                      {formatResourceAllocationDuration(line.orderedMinutes)}
                    </span>
                  </div>
                ))}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setOpen(false)}>
              Zamknij
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default EngagementHistoryDialog;
